import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { CoreApi } from '@inter-ikea-storageone/range';
import { RootState } from 'ducks';
import { Dispatch } from 'redux';
import { DesignInteractionEnum } from '@insights/insights-data-provider';
import { reportVpcInteraction } from 'analytics/reporters';
import { selectGroup } from './products';

export enum VpcSaveStatus {
  Unsaved,
  Saving,
  Saved
}

export interface VpcState {
  vpcCode: string | null;
  vpcImageUrl: string | null;
  saveStatus: VpcSaveStatus;
}

const initialState: VpcState = {
  vpcCode: null,
  vpcImageUrl: null,
  saveStatus: VpcSaveStatus.Unsaved
};

// Complicated Actions

export type VpcReduxAction = ReturnType<
  typeof startVpcSave | typeof saveVpcCode | typeof selectGroup
>;
export const attachVpcSubscribers = (dispatch: Dispatch<VpcReduxAction>, coreApi: CoreApi) => {
  coreApi.subscribe('saveVpcStart', () => {
    dispatch(startVpcSave());
  });
  coreApi.subscribe('saveVpcComplete', vpcCode => {
    dispatch(saveVpcCode(vpcCode));

    // Every time we save a vpc we want to send this event
    reportVpcInteraction({
      vpcCode: vpcCode,
      interaction: DesignInteractionEnum.storeConfiguration
    });
  });
  coreApi.subscribe('loadVpc', () => {
    dispatch(selectGroup(''));
  });
};

// Slice that creates a reducer and actions

const vpcSlice = createSlice({
  name: 'vpc',
  initialState,
  reducers: {
    invalidateVpcCode(state) {
      Object.assign(state, initialState);
    },
    startVpcSave(state) {
      state.saveStatus = VpcSaveStatus.Saving;
    },
    saveVpcCode(state, action: PayloadAction<string>) {
      state.vpcCode = action.payload;
      state.saveStatus = VpcSaveStatus.Saved;
    },
    saveVpcImageUrl(state, action: PayloadAction<string>) {
      state.vpcImageUrl = action.payload;
    }
  }
});

// Selectors

export const getVpcCode = (state: RootState) => state.vpc.vpcCode;
export const getVpcImageUrl = (state: RootState) => state.vpc.vpcImageUrl;
export const getVpcSaveStatus = (state: RootState) => state.vpc.saveStatus;

// Exports

export const { invalidateVpcCode, startVpcSave, saveVpcCode, saveVpcImageUrl } = vpcSlice.actions;

export default vpcSlice.reducer;
